const STORAGE_KEY = 'regai-consent';
const GTM_ID = import.meta.env.VITE_GTM_ID ?? 'GTM-MSB5N898';
const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID ?? '';
const isBrowser = typeof window !== 'undefined';

type ConsentValue = 'granted' | 'denied';

export type ConsentPreferences = {
  analytics: boolean;
  marketing: boolean;
  updatedAt: string;
};

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

let gtmLoaded = false;
let metaPixelLoaded = false;

const ensureDataLayer = () => {
  if (!isBrowser) return;
  if (!window.dataLayer) {
    window.dataLayer = [] as unknown[];
  }
  if (!window.gtag) {
    window.gtag = (...args: unknown[]) => {
      (window.dataLayer as unknown[]).push(args);
    };
  }
};

const updateConsentMode = (analytics: ConsentValue, marketing: ConsentValue) => {
  ensureDataLayer();
  window.gtag?.('consent', 'update', {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
  });
};

const bootstrapGTM = () => {
  if (!isBrowser || gtmLoaded) return;
  ensureDataLayer();
  (window.dataLayer as unknown[]).push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(GTM_ID)}`;
  document.head.appendChild(script);
  gtmLoaded = true;
};

const bootstrapMetaPixel = () => {
  if (!isBrowser || metaPixelLoaded || !META_PIXEL_ID) return;
  if (!window.fbq) {
    type FBQFunction = ((...args: unknown[]) => void) & {
      queue?: unknown[][];
      loaded?: boolean;
      version?: string;
      push?: FBQFunction;
    };

    const fbq: FBQFunction = function (...args: unknown[]) {
      fbq.queue = fbq.queue ?? [];
      fbq.queue.push(args);
    } as FBQFunction;

    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.push = fbq;
    window.fbq = fbq;

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    (document.head || document.body).appendChild(script);
  }

  window.fbq?.('init', META_PIXEL_ID);
  window.fbq?.('track', 'PageView');
  metaPixelLoaded = true;
};

export const getStoredConsent = (): ConsentPreferences | null => {
  try {
    if (!isBrowser) return null;
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentPreferences;
    if (typeof parsed.analytics === 'boolean' && typeof parsed.marketing === 'boolean') {
      return parsed;
    }
    return null;
  } catch (error) {
    console.warn('Failed to parse stored consent', error);
    return null;
  }
};

export const saveConsent = (prefs: ConsentPreferences) => {
  if (!isBrowser) return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
};

export const clearConsent = () => {
  if (!isBrowser) return;
  window.localStorage.removeItem(STORAGE_KEY);
  updateConsentMode('denied', 'denied');
};

export const applyConsentPreferences = (prefs: ConsentPreferences) => {
  if (!isBrowser) return;
  updateConsentMode(prefs.analytics ? 'granted' : 'denied', prefs.marketing ? 'granted' : 'denied');

  if (prefs.analytics) {
    bootstrapGTM();
  }

  if (prefs.marketing) {
    bootstrapMetaPixel();
  }
};

export const initializeConsent = () => {
  if (!isBrowser) return null;
  ensureDataLayer();
  window.gtag?.('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });

  const stored = getStoredConsent();
  if (stored) {
    applyConsentPreferences(stored);
  }
  return stored;
};

export const createConsentPreferences = (
  overrides?: Partial<Pick<ConsentPreferences, 'analytics' | 'marketing'>>,
): ConsentPreferences => ({
  analytics: overrides?.analytics ?? false,
  marketing: overrides?.marketing ?? false,
  updatedAt: new Date().toISOString(),
});
